import React, { useState, useEffect } from 'react';
import { AlertPopup } from './common/AlertPopup';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';

export function SchedulingMetricsModal({ isOpen, onClose, metricType, currentValues, projectData }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [values, setValues] = useState({
    used: currentValues?.used || '',
    total: currentValues?.total || ''
  });
  const [errors, setErrors] = useState({
    used: '',
    total: ''
  });
  const [alert, setAlert] = useState({ show: false, message: '', isError: false });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset form values when currentValues changes
  useEffect(() => {
    setValues({
      used: currentValues?.used || '',
      total: currentValues?.total || ''
    });
  }, [currentValues]);

  // Reset success state when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setIsSuccess(false);
    }
  }, [isOpen]);

  const getTitle = () => {
    switch (metricType) {
      case 'mobs':
        return 'Update Mobilizations';
      case 'days':
        return 'Update Days';
      case 'trips':
        return 'Update Trips';
      default:
        return 'Update Metrics';
    }
  };

  const getLabels = () => {
    switch (metricType) {
      case 'mobs':
        return { used: 'Mobs Used', total: 'Mobs Contracted' };
      case 'days':
        return { used: 'Days Used', total: 'Days Estimated' };
      case 'trips':
        return { used: 'Trips Used', total: 'Trips Estimated' };
      default:
        return { used: 'Used', total: 'Total' };
    }
  };

  const validateValues = () => {
    // Remove the validation check for used > total
    return true;
  };

  const handleInputChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Remove the validation check for used > total
    setErrors(prev => ({ ...prev, used: '' }));
  };

  const handleSubmit = async () => {
    if (!validateValues()) {
      return;
    }

    if (!projectData?.opportunityUUID) {
      setAlert({
        show: true,
        message: 'Project data is missing',
        isError: true
      });
      return;
    }

    if (!isReady) {
      return;
    }

    setIsLoading(true);
    try {
      // Don't convert to integer, use the string directly
      const opportunityId = projectData.opportunityUUID;
      
      const payload = {
        Opportunity_UUID__c: opportunityId, // Use the string directly
        mobs_used: metricType === 'mobs' ? parseInt(values.used) || 0 : 0,
        mobs_contracted: metricType === 'mobs' ? parseInt(values.total) || 0 : 0,
        days_used: metricType === 'days' ? parseInt(values.used) || 0 : 0,
        days_estimated: metricType === 'days' ? parseInt(values.total) || 0 : 0,
        trip_used: metricType === 'trips' ? parseInt(values.used) || 0 : 0,
        trip_estimated: metricType === 'trips' ? parseInt(values.total) || 0 : 0
      };

      try {
        // First check if schedule exists - use the string ID
        const checkResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules/${opportunityId}`,
          await getAuthConfig()
        );

        // If schedule exists, preserve existing values
        if (checkResponse.data.status === "success" && checkResponse.data.data) {
          const existingData = checkResponse.data.data;
          payload.mobs_used = metricType === 'mobs' ? parseInt(values.used) || 0 : existingData.mobs_used;
          payload.mobs_contracted = metricType === 'mobs' ? parseInt(values.total) || 0 : existingData.mobs_contracted;
          payload.days_used = metricType === 'days' ? parseInt(values.used) || 0 : existingData.days_used;
          payload.days_estimated = metricType === 'days' ? parseInt(values.total) || 0 : existingData.days_estimated;
          payload.trip_used = metricType === 'trips' ? parseInt(values.used) || 0 : existingData.trip_used;
          payload.trip_estimated = metricType === 'trips' ? parseInt(values.total) || 0 : existingData.trip_estimated;

          // PUT request for existing schedule
          await axios.put(
            `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules/${opportunityId}`,
            payload,
            await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
          );
        } else {
          throw new Error('Schedule not found'); // Force catch block to do POST
        }
      } catch (error) {
        // If schedule doesn't exist or GET fails, do POST request
        await axios.post(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules`,
          payload,
          await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
        );
      }

      setIsSuccess(true);
    } catch (error) {
      console.error("Error updating schedule:", error);
      setAlert({
        show: true,
        message: error.message === 'Invalid Opportunity Number' 
          ? 'Invalid Opportunity Number format'
          : 'Failed to update schedule. Please try again.',
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ show: false, message: '', isError: false });
  };

  const handleClose = (success = false) => {
    if (success && isSuccess) { // Only trigger parent refresh if we're closing after a successful update
      onClose(true);
    } else {
      onClose(false);
    }
    setValues({ used: '', total: '' });
    setIsSuccess(false);
  };

  if (!isOpen) return null;

  const labels = getLabels();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start max-md:items-center justify-center max-md:px-4 max-md:py-4 z-50 overflow-y-auto">
      {alert.show && (
        <AlertPopup
          message={alert.message}
          onClose={handleCloseAlert}
          isError={alert.isError}
        />
      )}
      <div className="relative w-1/2 max-md:w-full max-md:max-w-lg bg-white rounded-lg mt-[175px] max-md:mt-8">
        <button
          onClick={() => handleClose()}
          className="absolute -top-10 -right-1 max-md:-top-8 max-md:-right-2 text-white text-3xl font-normal cursor-pointer z-50"
        >
          ×
        </button>

        <div className="sticky top-0 bg-white p-6 max-md:p-4 border-b border-gray-200 rounded-t-lg z-40 shadow-sm">
          <h2 className="text-xl max-md:text-lg font-semibold">{getTitle()}</h2>
        </div>

        <div className="p-6 max-md:p-4">
          {isSuccess ? (
            <div className="min-h-[200px] max-md:min-h-[160px] flex flex-col items-center justify-center">
              <div className="w-16 h-16 max-md:w-14 max-md:h-14 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 max-md:w-7 max-md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl max-md:text-xl font-semibold text-gray-800 mb-4 text-center">
                Schedule Updated Successfully!
              </h2>
              <button
                onClick={() => handleClose(true)}
                className="bg-red-700 text-white py-2 px-8 rounded-lg hover:bg-red-800 max-md:w-full"
              >
                Done
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-2 max-md:grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm max-md:text-xs text-gray-600 mb-1">{labels.used}</label>
                  <input
                    type="number"
                    min="0"
                    value={values.used}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || parseInt(newValue) >= 0) {
                        handleInputChange('used', newValue);
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleInputChange('used', '0');
                      }
                    }}
                    className={`w-full p-2 max-md:p-2 border rounded-md text-sm max-md:text-xs ${errors.used ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.used && (
                    <p className="text-red-500 text-sm max-md:text-xs mt-1">{errors.used}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm max-md:text-xs text-gray-600 mb-1">{labels.total}</label>
                  <input
                    type="number"
                    min="0"
                    value={values.total}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || parseInt(newValue) >= 0) {
                        handleInputChange('total', newValue);
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleInputChange('total', '0');
                      }
                    }}
                    className="w-full p-2 max-md:p-2 border rounded-md text-sm max-md:text-xs"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  className="w-1/4 max-md:w-full bg-red-700 text-white py-2 px-4 rounded-lg hover:bg-red-800 disabled:opacity-50"
                  onClick={handleSubmit}
                  disabled={isLoading || errors.used !== ''}
                >
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
} 
